import { useState } from 'react';
import { api } from '../../shared/api/client';
import type { Family, FamilyInvitation } from '../../shared/api/types';
import { Button, Card, ErrorState, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { MyFamilyCard } from '../profile/MyFamily';
import { useMe } from '../session/SessionProvider';

/**
 * Mi familia (antes «Mi hogar»): LoveHome vive dentro de KEORA. Se pide un boleto de un solo uso y LoveHome se abre ya
 * con la cuenta y la familia de KEORA, sin otra contraseña. Ocupa toda la página para que se sienta
 * parte de KEORA y no otra aplicación. Abre siempre directo (LoveHome puede tener ya la familia
 * enlazada a la cuenta); quien aún no tiene Mi familia en KEORA ve un aviso corto para formarla aquí,
 * o la invitación que ya le mandaron para que no forme otra.
 */
export function HomeLovePage() {
  const me = useMe();
  const families = useAsync((signal) => api.get<{ items: Family[] }>('/identity/me/families', signal).then((r) => r.items.length), [me.personId]);
  const invitations = useAsync((signal) => api.get<{ items: FamilyInvitation[] }>('/identity/me/family-invitations', signal).then((r) => r.items.length), [me.personId]);
  const [forming, setForming] = useState(false);
  const [count, setCount] = useState<number | null>(null);
  const [frameKey, setFrameKey] = useState(0);

  const noFamily = families.status === 'ready' && families.data === 0;
  // Si ya la invitaron a una familia, se le muestra para aceptarla en vez de formar otra.
  const invited = noFamily && invitations.status === 'ready' && invitations.data > 0;
  if (invited) {
    return (
      <div className="stack">
        <p className="muted">Te invitaron a una familia. Acéptala para entrar a Mi familia con ella.</p>
        <MyFamilyCard
          personId={me.personId}
          onFamilies={(n) => {
            if (n > 0) {
              families.reload();
              invitations.reload();
            }
          }}
        />
      </div>
    );
  }
  return (
    <>
      {noFamily && !forming ? (
        <Card title="Aún no formas tu familia">
          <p className="muted">Agrega a tu esposo o esposa y a tus hijos para que entren juntos a Mi familia y la iglesia los tenga ligados (por ejemplo, para la guardería).</p>
          <Button type="button" onClick={() => setForming(true)}>
            Formar mi familia
          </Button>
        </Card>
      ) : null}
      {noFamily && forming ? (
        <div className="stack">
          <p className="muted">Usa los mismos nombres que ya tengan en Mi familia para que no se dupliquen.</p>
          <MyFamilyCard personId={me.personId} onFamilies={setCount} />
          <Button
            type="button"
            disabled={!count}
            onClick={() => {
              setForming(false);
              families.reload();
              setFrameKey((k) => k + 1);
            }}
          >
            Listo, abrir Mi familia
          </Button>
        </div>
      ) : (
        <HomeFrame key={frameKey} />
      )}
    </>
  );
}

/**
 * ¿Se abre Mi hogar porque la persona recargó la página estando en él? Solo la primera vez desde que
 * cargó la página: entrar desde el menú después ya no cuenta como recarga.
 */
let firstOpen = true;
function openedByReload(): boolean {
  const first = firstOpen;
  firstOpen = false;
  if (!first) return false;
  try {
    const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    return nav?.type === 'reload';
  } catch {
    return false;
  }
}

function HomeFrame() {
  // Al recargar, Mi hogar vuelve a donde ibas; al entrar desde el menú, empieza en su inicio.
  const [restore] = useState(openedByReload);
  const entry = useAsync(() => api.post<{ url: string }>('/hogar/boleto'), []);
  if (entry.status === 'loading') return <Loading label="Abriendo Mi familia…" />;
  if (entry.status === 'error') return <ErrorState error={entry.error} onRetry={entry.reload} />;
  const src = restore ? `${entry.data.url}&recarga=1` : entry.data.url;
  return <iframe className="hogar-frame" title="Mi familia" src={src} allow="clipboard-write; web-share" />;
}
